<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Move Datasets to Recycle Bin</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="warning-text">
          Do you want to move the following datasets to the recycle bin?
        </div>

        <div class="section">
          <h3>Datasets to Move</h3>
          <div class="datasets-list">
            <div v-for="dataset in selectedDatasets" :key="dataset.id" class="dataset-item">
              <div class="dataset-id">{{ toHex(dataset.id) }}</div>
              <div class="dataset-name">{{ dataset.name || 'Untitled' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-delete"
          :disabled="selectedDatasets.length === 0"
          @click="handleDelete()"
        >
          Move to Recycle Bin
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const props = defineProps({
  selectedDatasets: {
    type: Array,
    default: () => [],
  },
  currentProjectId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['cancelled', 'delete-datasets']);

const isOpen = ref(false);

const toHex = (id) => {
  return "d-" + id.toString(16);
};

const openModal = () => {
  isOpen.value = true;
};

const closeModal = () => {
  isOpen.value = false;
};

const handleCancel = () => {
  closeModal();
  emit('cancelled');
};

const handleDelete = async () => {

  if (props.selectedDatasets.length === 0) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'No datasets selected to delete',
      type: 'warning',
      position: 'top',
    });
    return;
  }

  try {
    const datasetIds = props.selectedDatasets.map(dataset => dataset.id);
    const projectId = props.currentProjectId;

    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.deleteDataset({
      ids: datasetIds,
    });

    if (response && response.result) {
      emit('delete-datasets', {
        datasets: props.selectedDatasets,
        success: true,
      });
      closeModal();
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Successfully deleted ${response.result.length} dataset(s)`,
        type: 'success',
        position: 'top',
      });
    } else {
      const errorMessage = response?.error?.message || 'Failed to delete datasets';
      console.error('Failed to delete datasets:', errorMessage);
      $toast.open({
        queue: true,
        dismissible: true,
        message: errorMessage,
        type: 'error',
        position: 'top',
      });
    }
  } catch (error) {
    const errorMessage = error?.message || 'Error deleting datasets';
    console.error('Error deleting datasets:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  }
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.warning-text {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #d31901;
  line-height: 1.5;
}

.section {
  margin-bottom: 20px;

  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.datasets-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.dataset-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 13px;

  .dataset-id {
    font-weight: 600;
    color: #666;
    min-width: 80px;
  }

  .dataset-name {
    flex: 1;
    color: #333;
  }
}

</style>
